import { DefaultOptionType } from 'antd/es/select';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { IStatus, StatusCategory } from '@/pages/project-management/task/models';

import { BaseButton } from '@/components/base-button/BaseButton';
import { FormInputDate } from '@/components/form-input-date/FormInputDate';
import { FormInputSelect } from '@/components/form-input-select/FormInputSelect';
import { FormSelect } from '@/components/form-select/FormSelect';

import useAuthorization from '@/hooks/useAuthorization';
import useFetch from '@/hooks/useFetch';

import { DataInput } from '@/utils/helpers/common';
import { MOT_OTHER_GROUPS_OPTION, MOT_OTHER_TASK_TYPE } from '@/utils/interfaces';
import { Ii18n } from '@/utils/interfaces/i18n';
import { CATEGORY_OPTIONS } from '@utils/constants';

import Minus from '@/assets/icons/Minus.svg';

interface IFilterProps {
  onSubmit: (values: any) => void;
  onReset?: () => void;
  picOptions: DefaultOptionType[];
  defaultValues: { [key: string]: any };
}

const TaskFormFilter = ({ onSubmit, picOptions, defaultValues }: IFilterProps) => {
  const { t }: Ii18n = useTranslation();
  const form = useForm({ defaultValues });
  const { isInternalRole } = useAuthorization();
  const { data: listStatus } = useFetch<IStatus[]>('/prj/status', 'GET');

  const categoryOption = isInternalRole
    ? CATEGORY_OPTIONS
    : CATEGORY_OPTIONS.filter((item) => MOT_OTHER_GROUPS_OPTION.includes(item.value as MOT_OTHER_TASK_TYPE));

  const statusOptions = listStatus?.reduce((options: DefaultOptionType[], item: IStatus) => {
    if (item.types?.includes(StatusCategory.TASK_LIST)) {
      options.push({
        label: item.name,
        value: item.name
      });
    }
    return options;
  }, []);

  const disabledButtonReset = () => {
    const { category, status, deadlineDateFrom, deadlineDateTo, picIds } = form.watch();
    return !category && !status && !deadlineDateFrom && !deadlineDateTo && !picIds;
  };

  const handleReset = () => {
    form.reset({
      category: [],
      status: null,
      deadlineDateFrom: null,
      deadlineDateTo: null,
      picIds: null
    });
  };

  const handleSubmit = (values: any) => {
    const { deadlineDateFrom, deadlineDateTo } = values ?? {};
    const convertValue = {
      ...values,
      deadlineDateFrom: DataInput.formatTime(deadlineDateFrom),
      deadlineDateTo: DataInput.formatTime(deadlineDateTo)
    };
    onSubmit(convertValue);
  };

  return (
    <>
      <FormProvider {...form}>
        <div className='p-[20px] w-[680px] flex flex-col gap-[12px] task-form-filter'>
          <div className='text-20 font-bold'>{t('button:filter')}</div>
          <div className='flex gap-[16px]'>
            <FormSelect
              mode='multiple'
              name='category'
              label={t('project_detail:task_list:task_category')}
              options={categoryOption}
              placeholder={`${t('common:MSG_001_select', { field: t('project_detail:task_list:task_category') })}`}
            />
            <FormInputSelect
              name='status'
              labelTx={t('project_detail:task_list:status')}
              options={statusOptions}
              placeholderTx={`${t('common:MSG_001_select', { field: t('project_detail:task_list:status') })}`}
              listHeight={800}
            />
          </div>
          <div className='flex gap-[16px]'>
            <FormInputDate
              name='deadlineDateFrom'
              labelTx='project_detail:task_list:deadline'
              placeholder={t('placeholder:date')}
              disabledDate={(d) => {
                return Boolean(form.getValues('deadlineDateTo') && d.startOf('day').isAfter(form.getValues('deadlineDateTo')));
              }}
            />
            <div className='mt-10'>
              <Minus />
            </div>
            <FormInputDate
              name='deadlineDateTo'
              label='&nbsp;'
              placeholder={t('placeholder:date')}
              disabledDate={(d) => {
                return Boolean(form.getValues('deadlineDateFrom') && d.endOf('day').isBefore(form.getValues('deadlineDateFrom')));
              }}
            />
          </div>
          <FormSelect
            mode='multiple'
            name='picIds'
            label={String(t('project_detail:task_list:task_pic'))}
            placeholder={String(
              t('placeholder:select', {
                field: t('project_detail:task_list:task_pic')
              })
            )}
            options={picOptions}
          />
          <div className='flex justify-end space-x-2'>
            <div className='w-[120px]'>
              <BaseButton type='tertiary' size='medium' disabled={disabledButtonReset()} onClick={handleReset}>
                {t('master_data:document_management:btn_reset')}
              </BaseButton>
            </div>
            <div className='w-[120px]'>
              <BaseButton type='primary' size='medium' onClick={form.handleSubmit(handleSubmit)}>
                <span>{t('master_data:document_management:btn_apply')} </span>
              </BaseButton>
            </div>
          </div>
        </div>
      </FormProvider>
    </>
  );
};

export default TaskFormFilter;
